/* ============================================
   KOLLAKSEKOLLA — GLOBAL EFFECTS JS
   ============================================ */

(function() {
  'use strict';

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReducedMotion) return;

  // ============================================
  // 1. SCROLL FADE-IN ANIMATIONS
  // ============================================
  function initScrollAnimations() {
    const sectionsToAnimate = document.querySelectorAll(
      '.shopify-section, .kk-cta, .kk-why, .kk-gallery, .kk-story, .kk-news, .kk-marquee'
    );

    sectionsToAnimate.forEach(section => {
      if (!section.classList.contains('kk-fade-up') && !section.classList.contains('kk-no-animate')) {
        section.classList.add('kk-fade-up');
      }
    });

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('kk-visible');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.12,
      rootMargin: '0px 0px -60px 0px'
    });

    document.querySelectorAll('.kk-fade-up').forEach(el => observer.observe(el));
  }

  // ============================================
  // 2. CUSTOM CURSOR (Desktop only)
  // ============================================
  function initCustomCursor() {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    const cursor = document.createElement('div');
    cursor.className = 'kk-cursor';
    document.body.appendChild(cursor);

    const dot = document.createElement('div');
    dot.className = 'kk-cursor-dot';
    document.body.appendChild(dot);

    let mouseX = window.innerWidth / 2, mouseY = window.innerHeight / 2;
    let cursorX = mouseX, cursorY = mouseY;
    let dotX = mouseX, dotY = mouseY;
    let isVisible = false;

    cursor.classList.add('kk-cursor-hidden');
    dot.classList.add('kk-cursor-hidden');

    document.addEventListener('mousemove', (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      if (!isVisible) {
        cursor.classList.remove('kk-cursor-hidden');
        dot.classList.remove('kk-cursor-hidden');
        isVisible = true;
      }
    });

    document.addEventListener('mouseleave', () => {
      cursor.classList.add('kk-cursor-hidden');
      dot.classList.add('kk-cursor-hidden');
      isVisible = false;
    });

    document.addEventListener('mousedown', () => cursor.classList.add('kk-cursor-click'));
    document.addEventListener('mouseup', () => cursor.classList.remove('kk-cursor-click'));

    function animate() {
      cursorX += (mouseX - cursorX) * 0.18;
      cursorY += (mouseY - cursorY) * 0.18;
      dotX += (mouseX - dotX) * 0.5;
      dotY += (mouseY - dotY) * 0.5;

      cursor.style.transform = `translate(${cursorX}px, ${cursorY}px) translate(-50%, -50%)`;
      dot.style.transform = `translate(${dotX}px, ${dotY}px) translate(-50%, -50%)`;

      requestAnimationFrame(animate);
    }
    animate();

    function attachHoverListeners() {
      const interactives = document.querySelectorAll(
        'a, button, input, textarea, select, [role="button"], .kk-magnetic, .kk-gallery-item, .kk-why-card'
      );
      interactives.forEach(el => {
        if (el.dataset.kkHoverAttached) return;
        el.dataset.kkHoverAttached = 'true';
        el.addEventListener('mouseenter', () => cursor.classList.add('kk-cursor-hover'));
        el.addEventListener('mouseleave', () => cursor.classList.remove('kk-cursor-hover'));
      });
    }
    attachHoverListeners();

    const mo = new MutationObserver(() => attachHoverListeners());
    mo.observe(document.body, { childList: true, subtree: true });

    document.addEventListener('focusin', (e) => {
      if (e.target.tagName !== 'SELECT') return;
      cursor.classList.add('kk-cursor-hidden');
      dot.classList.add('kk-cursor-hidden');
      document.body.classList.add('kk-select-open');
    });

    document.addEventListener('focusout', (e) => {
      if (e.target.tagName !== 'SELECT') return;
      document.body.classList.remove('kk-select-open');
      if (isVisible) {
        cursor.classList.remove('kk-cursor-hidden');
        dot.classList.remove('kk-cursor-hidden');
      }
    });
  }

  // ============================================
  // 3. MAGNETIC BUTTONS
  // ============================================
  function initMagneticButtons() {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    const buttonsToMagnetize = document.querySelectorAll(
      '.kk-cta-btn, .kk-news-btn, .kk-gallery-tag'
    );
    buttonsToMagnetize.forEach(btn => btn.classList.add('kk-magnetic'));

    document.querySelectorAll('.kk-magnetic').forEach(el => {
      el.addEventListener('mousemove', (e) => {
        const rect = el.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;
        const strength = 0.25;
        el.style.transform = `translate(${x * strength}px, ${y * strength}px)`;
      });
      el.addEventListener('mouseleave', () => {
        el.style.transform = 'translate(0, 0)';
      });
    });
  }

  // ============================================
  // 4. TEXT REVEAL
  // ============================================
  function initTextReveal() {
    const headlines = document.querySelectorAll('.kk-text-reveal');

    headlines.forEach(headline => {
      const text = headline.textContent;
      headline.textContent = '';
      [...text].forEach(char => {
        const span = document.createElement('span');
        span.className = 'kk-char';
        span.textContent = char === ' ' ? ' ' : char;
        headline.appendChild(span);
      });
    });

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const chars = entry.target.querySelectorAll('.kk-char');
          chars.forEach((char, i) => {
            char.style.transitionDelay = `${i * 0.025}s`;
          });
          entry.target.classList.add('kk-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.3 });

    headlines.forEach(h => observer.observe(h));
  }

  // ============================================
  // 5. MODAL DETECTION
  // ============================================
  function initModalDetection() {
    function checkModals() {
      const hasOpenModal = !!document.querySelector(
        'dialog[open], [role="dialog"][open], .cart-drawer__dialog[open], dialog.dialog-modal[open]'
      );
      document.body.classList.toggle('kk-modal-open', hasOpenModal);
    }

    const mo = new MutationObserver(checkModals);
    mo.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['open']
    });

    checkModals();
  }

  // ============================================
  // 7. HOLOGRAPHIC CARD EFFECT
  // ============================================
  function initHolographicCards() {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    function addHoloLayer(card) {
      if (card.querySelector('.kk-holo-layer')) return;
      const layer = document.createElement('div');
      layer.className = 'kk-holo-layer';
      layer.setAttribute('aria-hidden', 'true');
      card.appendChild(layer);
    }

    function attachHoloListener(card) {
      if (card.dataset.kkHoloAttached) return;
      card.dataset.kkHoloAttached = 'true';
      addHoloLayer(card);

      card.addEventListener('mousemove', (e) => {
        const rect = card.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        const angle = Math.atan2(
          e.clientY - (rect.top + rect.height / 2),
          e.clientX - (rect.left + rect.width / 2)
        ) * (180 / Math.PI) + 180;
        const dist = Math.sqrt(Math.pow((x - 50) / 50, 2) + Math.pow((y - 50) / 50, 2));
        card.style.setProperty('--kk-holo-x', `${x.toFixed(1)}%`);
        card.style.setProperty('--kk-holo-y', `${y.toFixed(1)}%`);
        card.style.setProperty('--kk-holo-angle', `${angle.toFixed(1)}deg`);
        card.style.setProperty('--kk-holo-opacity', String(Math.min(0.28, dist * 0.38)));
      });

      card.addEventListener('mouseleave', () => {
        card.style.removeProperty('--kk-holo-x');
        card.style.removeProperty('--kk-holo-y');
        card.style.removeProperty('--kk-holo-angle');
        card.style.removeProperty('--kk-holo-opacity');
      });
    }

    function scan() {
      document.querySelectorAll('product-card').forEach(attachHoloListener);
    }
    scan();
    new MutationObserver(scan).observe(document.body, { childList: true, subtree: true });
  }

  // ============================================
  // 8. KINETIC TYPOGRAPHY — WAVE CHARACTER RIPPLE
  // ============================================
  function initKineticTypography() {
    const hero = document.querySelector('.hero');
    if (!hero) return;

    // Find the jumbo/hero heading containing the brand text
    const targets = Array.from(hero.querySelectorAll('*')).filter(el => {
      const t = el.textContent.trim();
      return (t.includes('KOLLA') || t.includes('kolla')) &&
             el.children.length === 0 &&
             el.textContent.length < 80;
    });

    if (!targets.length) return;

    targets.forEach(el => {
      const originalText = el.textContent;
      const parent = el.parentElement;

      // Wrap in container
      const wrap = document.createElement('span');
      wrap.className = 'kk-kinetic-wrap';
      wrap.setAttribute('aria-label', originalText);

      // Split into chars
      [...originalText].forEach((char, i) => {
        const span = document.createElement('span');
        span.className = 'kk-kinetic-char';
        span.setAttribute('aria-hidden', 'true');
        span.textContent = char === ' ' ? ' ' : char;
        span.dataset.index = i;
        wrap.appendChild(span);
      });

      el.textContent = '';
      el.appendChild(wrap);
    });

    const chars = hero.querySelectorAll('.kk-kinetic-char');
    const totalChars = chars.length;

    // Animate on scroll: wave ripple across characters
    let ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const heroH = hero.offsetHeight;
        const scrollY = window.scrollY;
        const progress = Math.min(1, scrollY / (heroH * 0.55));

        chars.forEach((char, i) => {
          // Wave: each char has a phase offset — ripple moves left to right
          const phase = i / Math.max(1, totalChars - 1);
          // Letter-spacing opens up as you scroll (tight → spread)
          const spacing = (-0.04 + progress * 0.14).toFixed(3);
          // Vertical wave — chars float up with slight stagger
          const waveY = Math.sin(phase * Math.PI * 2 - progress * Math.PI * 3) * (progress * 6);
          // Color: white (#faf7f2) → lime (#C5FF3D) at peak scroll per char
          const charProgress = Math.max(0, Math.min(1, (progress - phase * 0.3) * 2));
          const r = Math.round(250 + (197 - 250) * charProgress);
          const g = Math.round(247 + (255 - 247) * charProgress);
          const b = Math.round(242 + (61 - 242) * charProgress);

          char.classList.add('kk-wave-ready');
          char.style.setProperty('--kk-char-offset', `${(-waveY).toFixed(2)}px`);
          char.style.setProperty('--kk-char-color', `rgb(${r},${g},${b})`);
          char.style.setProperty('--kk-char-spacing', `${spacing}em`);
          char.style.transform = `translateY(${(-waveY).toFixed(2)}px)`;
          char.style.color = `rgb(${r},${g},${b})`;
          char.style.letterSpacing = `${spacing}em`;
        });

        ticking = false;
      });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    // Initial entry animation: chars drop in from above on load
    requestAnimationFrame(() => {
      chars.forEach((char, i) => {
        char.style.opacity = '0';
        char.style.transform = 'translateY(-24px)';
        char.style.transition = `opacity 0.6s ease ${i * 0.028}s, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${i * 0.028}s, color 0.8s ease, letter-spacing 0.8s cubic-bezier(0.16,1,0.3,1)`;
        setTimeout(() => {
          char.style.opacity = '1';
          char.style.transform = 'translateY(0)';
        }, 80 + i * 28);
      });
    });

    onScroll();
  }

  // ============================================
  // 10. CURSOR PARTICLE TRAIL
  // ============================================
  function initCursorTrail() {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    const colors = ['#C5FF3D', '#FF3D7F', '#faf7f2', '#0A0A0A'];
    let lastX = -999, lastY = -999;

    document.addEventListener('mousemove', (e) => {
      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      if (Math.sqrt(dx * dx + dy * dy) < 8) return;
      lastX = e.clientX;
      lastY = e.clientY;

      const dot = document.createElement('div');
      dot.className = 'kk-trail-dot';
      dot.style.background = colors[Math.floor(Math.random() * colors.length)];
      dot.style.left = e.clientX + 'px';
      dot.style.top = e.clientY + 'px';
      dot.style.width = (4 + Math.random() * 5) + 'px';
      dot.style.height = dot.style.width;
      document.body.appendChild(dot);

      dot.addEventListener('animationend', () => dot.remove(), { once: true });
    });
  }

  // ============================================
  // 11. 3D CARD TILT
  // ============================================
  function init3DTilt() {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    function attachTilt(card) {
      if (card.dataset.kkTiltAttached) return;
      card.dataset.kkTiltAttached = 'true';

      const inner = card.querySelector('.product-card__content');
      if (!inner) return;

      card.addEventListener('mousemove', (e) => {
        const rect = card.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width - 0.5;
        const y = (e.clientY - rect.top) / rect.height - 0.5;
        inner.style.transform = `rotateY(${(x * 8).toFixed(2)}deg) rotateX(${(-y * 8).toFixed(2)}deg) translateZ(4px)`;
      });

      card.addEventListener('mouseleave', () => {
        inner.style.transform = 'rotateY(0deg) rotateX(0deg) translateZ(0px)';
      });
    }

    function scan() {
      document.querySelectorAll('product-card').forEach(attachTilt);
    }
    scan();
    new MutationObserver(scan).observe(document.body, { childList: true, subtree: true });
  }

  // ============================================
  // 12. SCROLL COUNTER
  // ============================================
  function initScrollCounter() {
    const counters = document.querySelectorAll('[data-kk-counter]');
    if (!counters.length) return;

    function easeOutQuart(t) { return 1 - Math.pow(1 - t, 4); }

    function animateCounter(el) {
      if (el.dataset.kkCounterDone) return;
      el.dataset.kkCounterDone = 'true';

      const target = parseFloat(el.dataset.kkCounter);
      const duration = 1800;
      const start = performance.now();

      function step(now) {
        const progress = Math.min((now - start) / duration, 1);
        const value = target * easeOutQuart(progress);
        el.textContent = Number.isInteger(target)
          ? Math.round(value).toLocaleString()
          : value.toFixed(1);
        if (progress < 1) requestAnimationFrame(step);
        else el.textContent = Number.isInteger(target)
          ? target.toLocaleString()
          : target.toFixed(1);
      }
      requestAnimationFrame(step);
    }

    const obs = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          animateCounter(entry.target);
          obs.unobserve(entry.target);
        }
      });
    }, { threshold: 0.4 });

    counters.forEach(el => obs.observe(el));
  }

  // ============================================
  // INIT ALL
  // ============================================
  function init() {
    if (document.body.classList.contains('template-product')) return;
    initScrollAnimations();
    initCustomCursor();
    initMagneticButtons();
    initTextReveal();
    initModalDetection();
    initHolographicCards();
    initKineticTypography();
    initCursorTrail();
    init3DTilt();
    initScrollCounter();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
(function(){
  let overlay, modal, mapHome, confirmBar, activeMapPoll;

  function buildModal(){
    if(overlay) return;
    overlay = document.createElement('div');
    overlay.className = 'kk-locker-overlay';
    modal = document.createElement('div');
    modal.className = 'kk-locker-modal';

    const close = document.createElement('button');
    close.className = 'kk-locker-close';
    close.type = 'button';
    close.setAttribute('aria-label','Κλείσιμο');
    close.innerHTML = '&times;';
    modal.appendChild(close);

    confirmBar = document.createElement('div');
    confirmBar.className = 'kk-locker-confirm';
    modal.appendChild(confirmBar);

    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    close.addEventListener('click', function(e){
      e.preventDefault(); e.stopPropagation(); closeModal();
    });
    overlay.addEventListener('click', function(e){
      if(e.target === overlay) closeModal();
    });
    document.addEventListener('keydown', function(e){
      if(e.key === 'Escape' && overlay.classList.contains('is-open')) closeModal();
    });
  }

  function getLockerName(){
    const details = document.querySelector('.tp-box-now-locker-selection__details');
    if(!details || details.classList.contains('tp-hidden') || details.offsetParent === null) return null;
    const txt = details.innerText || '';
    const m = txt.match(/Locker Name:\s*(.+)/i);
    return m ? m[1].trim() : null;
  }

  function showConfirm(name){
    if(!confirmBar) return;
    if(confirmBar.dataset.kkName === name) return;
    confirmBar.dataset.kkName = name;

    let textEl = confirmBar.querySelector('.kk-confirm-text strong');
    if(confirmBar.classList.contains('is-visible') && textEl){
      textEl.textContent = name;
      return;
    }
    confirmBar.innerHTML =
      '<div class="kk-confirm-text">✓ Επέλεξες: <strong>'+name+'</strong></div>' +
      '<div class="kk-confirm-btns">' +
        '<button type="button" class="kk-confirm-yes">Επιβεβαίωση</button>' +
      '</div>';
    confirmBar.classList.add('is-visible');
    confirmBar.querySelector('.kk-confirm-yes').addEventListener('click', closeModal);
  }

  function openModal(){
    if(activeMapPoll){ clearInterval(activeMapPoll); activeMapPoll = null; }
    const mc = document.querySelector('.tp-box-now-locker-map-container');
    if(!mc) return;
    buildModal();
    if(!mapHome){
      mapHome = document.createElement('div');
      mapHome.style.display = 'none';
      mc.parentNode.insertBefore(mapHome, mc);
    }
    confirmBar.classList.remove('is-visible');
    confirmBar.dataset.kkName = '';
    overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';

    var loader = document.createElement('div');
    loader.className = 'kk-map-loader';
    loader.style.cssText = 'text-align:center;padding:48px 0;color:#666;font-size:14px;font-family:inherit;';
    loader.textContent = 'Φόρτωση χάρτη…';
    modal.insertBefore(loader, confirmBar);

    function doMove(){
      if(loader.parentNode) loader.parentNode.removeChild(loader);
      mc.style.display = 'block';
      modal.insertBefore(mc, confirmBar);
      setTimeout(function(){ window.dispatchEvent(new Event('resize')); }, 100);
      setTimeout(function(){ window.dispatchEvent(new Event('resize')); }, 400);
    }

    if(mc.querySelector('.leaflet-container')){
      doMove();
      return;
    }

    var attempts = 0;
    activeMapPoll = setInterval(function(){
      if(!overlay.classList.contains('is-open')){
        clearInterval(activeMapPoll); activeMapPoll = null;
        if(loader.parentNode) loader.parentNode.removeChild(loader);
        return;
      }
      attempts++;
      if(mc.querySelector('.leaflet-container') || attempts >= 40){
        clearInterval(activeMapPoll); activeMapPoll = null;
        doMove();
      }
    }, 50);
  }

  function closeModal(){
    if(activeMapPoll){ clearInterval(activeMapPoll); activeMapPoll = null; }
    const loader = modal && modal.querySelector('.kk-map-loader');
    if(loader) loader.parentNode.removeChild(loader);
    if(!overlay || !overlay.classList.contains('is-open')) return;
    overlay.classList.remove('is-open');
    document.body.style.overflow = '';
    const mc = modal.querySelector('.tp-box-now-locker-map-container');
    if(mc && mapHome){
      mapHome.parentNode.insertBefore(mc, mapHome);
      mc.style.display = 'none';
    }
  }

  document.body.addEventListener('click', function(e){
    const btn = e.target.closest('.tp-box-now-locker-selection__btn');
    if(btn){ setTimeout(openModal, 100); }
  }, true);

  new MutationObserver(function(){
    if(!overlay || !overlay.classList.contains('is-open')) return;
    const name = getLockerName();
    if(name){
      showConfirm(name);
    }
  }).observe(document.body, {childList:true, subtree:true, attributes:true, attributeFilter:['style','class']});
})();
/* ===== Box Now: μετακίνηση κουμπιού στο summary ===== */
(function(){
  var revealTimer = setTimeout(function(){
    var el = document.querySelector('.tp-box-now-locker-selection__actions');
    if(el) el.style.visibility = 'visible';
  }, 1500);

  function kkMoveLocker(){
    const actions = document.querySelector('.tp-box-now-locker-selection__actions');
    if(!actions) return;
    actions.style.visibility = 'visible';
    clearTimeout(revealTimer);
    const ctas = document.querySelector('.cart__ctas');
    if(!ctas) return;
    if(actions.nextElementSibling !== ctas){
      ctas.parentNode.insertBefore(actions, ctas);
    }
  }
  kkMoveLocker();
  document.addEventListener('DOMContentLoaded', kkMoveLocker);
  document.addEventListener('cart:refresh', kkMoveLocker);
  document.addEventListener('cart:updated', kkMoveLocker);
  new MutationObserver(function(){
    const actions = document.querySelector('.tp-box-now-locker-selection__actions');
    const ctas = document.querySelector('.cart__ctas');
    if(actions && ctas && actions.nextElementSibling !== ctas){
      kkMoveLocker();
    }
  }).observe(document.body,{childList:true,subtree:true});
})();